import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { ChatCircleDots, MagnifyingGlass, Phone, EnvelopeSimple, ArrowSquareOut } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/admin/leads"), api.get("/admin/users")]).then(([l, u]) => {
      setLeads(l.data);
      setUsers(u.data);
    });
  }, []);

  const nurseryById = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const filtered = leads.filter((l) => {
    if (source !== "all" && l.source !== source) return false;
    if (!q) return true;
    const nursery = nurseryById[l.nursery_id];
    const blob = `${l.name} ${l.contact} ${l.interest} ${nursery?.nursery_name || ""}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  const sources = Array.from(new Set(leads.map((l) => l.source).filter(Boolean)));

  return (
    <div data-testid="admin-leads-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Inbound demand</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ marginTop: 8 }}>Customer leads</h1>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>{leads.length} leads across all nurseries · captured by chatbot, forms, and direct entry.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select className="input" value={source} onChange={(e) => setSource(e.target.value)} style={{ minWidth: 140 }} data-testid="lead-source-filter">
            <option value="all">All sources</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ position: "relative" }}>
            <MagnifyingGlass size={14} weight="bold" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted-fg)" }} />
            <input className="input" placeholder="Search name, contact, nursery…" value={q} onChange={(e) => setQ(e.target.value)} style={{ paddingLeft: 34, minWidth: 260 }} data-testid="lead-search-input" />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <ChatCircleDots size={40} weight="duotone" color="var(--muted-fg)" />
          <h3 className="font-display" style={{ fontSize: 22, marginTop: 12 }}>{leads.length === 0 ? "No leads yet" : "No matches"}</h3>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14 }}>
            {leads.length === 0
              ? "Once a nursery's chatbot captures a customer or someone fills a form, it will show up here."
              : "Try clearing your filters."}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "var(--bg-alt)", textAlign: "left" }}>
                <th style={th}>Customer</th>
                <th style={th}>Interest</th>
                <th style={th}>Nursery</th>
                <th style={th}>Source</th>
                <th style={th}>Status</th>
                <th style={th}>When</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const nursery = nurseryById[l.nursery_id];
                const isEmail = (l.contact || "").includes("@");
                return (
                  <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`lead-row-${l.id}`}>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{l.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--muted-fg)", marginTop: 2 }}>
                        {isEmail ? <EnvelopeSimple size={12} weight="bold" /> : <Phone size={12} weight="bold" />}
                        <a href={isEmail ? `mailto:${l.contact}` : `tel:${l.contact}`} style={{ color: "var(--secondary)", textDecoration: "underline" }}>{l.contact}</a>
                      </div>
                    </td>
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ fontSize: 13, color: "var(--text-soft)", lineHeight: 1.45 }}>{l.interest}</div>
                    </td>
                    <td style={td}>
                      {nursery ? (
                        <Link to={`/admin/users/${nursery.id}`} style={{ color: "var(--secondary)", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`lead-nursery-${l.id}`}>
                          {nursery.nursery_name} <ArrowSquareOut size={12} weight="bold" />
                        </Link>
                      ) : (
                        <span style={{ color: "var(--muted-fg)" }}>—</span>
                      )}
                    </td>
                    <td style={td}><span className="badge badge-soft">{l.source || "—"}</span></td>
                    <td style={td}><span className={`badge ${l.status === "new" ? "badge-warn" : "badge-success"}`}>{l.status || "new"}</span></td>
                    <td style={{ ...td, fontSize: 12.5, color: "var(--muted-fg)", whiteSpace: "nowrap" }}>{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: "12px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted-fg)" };
const td = { padding: "14px 16px", verticalAlign: "top" };
